import {openBankingConstants} from '../constants';
import {openBankingConnectionStatusConstants} from '../../constants';
// Import the leaf helper directly (NOT via the utils/helpers barrel): the barrel
// reaches React components/hooks, and pulling that graph into a store-init-time
// reducer closes a circular dependency that crashes the app at startup.
import {getObPaymentMethodOptionLabel} from '../../utils/helpers/expenses.helpers';

// Re-syncs each OB payment method option's bank name (and thus its label) against
// the currently loaded accounts, keyed by account_id -> account.name. Accounts are
// the source of truth for the bank name (it can be renamed after the option's
// snapshot was taken); an option whose account isn't loaded keeps its snapshot.
// Called whenever accounts are loaded/updated or new options arrive so the filter
// dropdown/tags reflect account renames.
const syncObOptionsWithAccounts = (options, accounts) => {
  if (!options.length || !accounts.length) return options;
  const nameById = new Map(accounts.map((account) => [account.id, account.name]));
  return options.map((option) => {
    if (!nameById.has(option.account_id)) return option;
    const bankName = nameById.get(option.account_id) ?? '';
    if (bankName === option.bank_name) return option;
    return {
      ...option,
      bank_name: bankName,
      label: getObPaymentMethodOptionLabel(option.iban, bankName),
    };
  });
};

const openBankingState = {
  accounts: [],
  isLoading: false,
  accountsLoaded: false,
  error: null,
  connections: [],
  isConnectionsLoading: false,
  connectionsError: null,
  isCreatingConnection: false,
  connectUrl: null,
  pendingConnectionId: null,
  connectionCreateError: null,
  accountsRefreshDone: null,
  firstNewAccountId: null,
  pendingAccountRefresh: null,
  hasAccountAccess: false,
  accountAccessLoaded: false,
  accountAccessIds: [],
  obPaymentMethodOptions: [],
};

const openBankingReducer = (state = openBankingState, action) => {
  const {type, data} = action;
  switch (type) {
    case openBankingConstants.SET_ACCOUNTS_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case openBankingConstants.SET_ACCOUNTS_LOADED:
      return {
        ...state,
        accountsLoaded: action.isLoaded,
      };
    case openBankingConstants.GET_ACCOUNTS: {
      const accounts = data || [];
      return {
        ...state,
        accounts,
        obPaymentMethodOptions: syncObOptionsWithAccounts(state.obPaymentMethodOptions, accounts),
        isLoading: false,
        accountsLoaded: true,
        error: null,
      };
    }
    case openBankingConstants.APPEND_ACCOUNTS: {
      const accounts = [...state.accounts, ...(data || [])];
      return {
        ...state,
        accounts,
        obPaymentMethodOptions: syncObOptionsWithAccounts(state.obPaymentMethodOptions, accounts),
        isLoading: false,
        accountsLoaded: true,
        error: null,
      };
    }
    case openBankingConstants.SET_ACCOUNTS_ERROR:
      return {
        ...state,
        isLoading: false,
        accountsLoaded: true,
        error: data,
      };
    case openBankingConstants.SET_CONNECTIONS_LOADING:
      return {
        ...state,
        isConnectionsLoading: action.isLoading,
      };
    case openBankingConstants.GET_CONNECTIONS:
      return {
        ...state,
        connections: data || [],
        isConnectionsLoading: false,
        connectionsError: null,
      };
    case openBankingConstants.APPEND_CONNECTIONS:
      return {
        ...state,
        connections: [...state.connections, ...(data || [])],
        isConnectionsLoading: false,
        connectionsError: null,
      };
    case openBankingConstants.SET_CONNECTIONS_ERROR:
      return {
        ...state,
        isConnectionsLoading: false,
        connectionsError: data,
      };
    case openBankingConstants.UPDATE_CONNECTION:
      return {
        ...state,
        connections: state.connections.map((c) => (c.id === data.id ? data : c)),
      };
    case openBankingConstants.UPDATE_CONNECTION_STATUS:
      return {
        ...state,
        connections: state.connections.map((c) =>
          c.id === data.connection_id
            ? {...c, status: data.status, ...(data.status === openBankingConnectionStatusConstants.REVOKED ? {consent_valid_until: null} : {})}
            : c
        ),
      };
    case openBankingConstants.UPDATE_ACCOUNT: {
      const accounts = state.accounts.map((a) => (a.id === data.id ? data : a));
      return {
        ...state,
        accounts,
        obPaymentMethodOptions: syncObOptionsWithAccounts(state.obPaymentMethodOptions, accounts),
      };
    }
    case openBankingConstants.UPDATE_ACCOUNTS: {
      // Refreshes existing accounts in place (e.g. updated balance) without
      // touching list order or accounts not present in `data`.
      const updatesById = new Map((data || []).map((a) => [a.id, a]));
      const accounts = state.accounts.map((a) => updatesById.get(a.id) ?? a);
      return {
        ...state,
        accounts,
        obPaymentMethodOptions: syncObOptionsWithAccounts(state.obPaymentMethodOptions, accounts),
      };
    }
    case openBankingConstants.SET_CONNECTION_CREATING:
      return {
        ...state,
        isCreatingConnection: true,
        connectUrl: null,
        pendingConnectionId: null,
        connectionCreateError: null,
      };
    case openBankingConstants.CREATE_CONNECTION:
      return {
        ...state,
        isCreatingConnection: false,
        connectUrl: data.connect_url,
        pendingConnectionId: data.channel_id,
      };
    case openBankingConstants.CREATE_CONNECTION_ERROR:
      return {
        ...state,
        isCreatingConnection: false,
        connectionCreateError: data,
      };
    case openBankingConstants.CLOSE_CONNECT_MODAL:
      return {
        ...state,
        connectUrl: null,
        pendingConnectionId: null,
        connectionCreateError: action.error ?? null,
      };
    case openBankingConstants.ADD_CONNECTION:
      return {
        ...state,
        connections: [...state.connections, data],
      };
    case openBankingConstants.REMOVE_CONNECTION:
      return {
        ...state,
        connections: state.connections.filter((c) => c.id !== action.id),
      };
    case openBankingConstants.ACCOUNTS_REFRESH_DONE:
      return {
        ...state,
        accountsRefreshDone: {connectionId: action.connectionId, at: action.at},
      };
    case openBankingConstants.SET_FIRST_NEW_ACCOUNT_ID:
      return {
        ...state,
        firstNewAccountId: action.id,
      };
    case openBankingConstants.CLEAR_FIRST_NEW_ACCOUNT_ID:
      return {
        ...state,
        firstNewAccountId: null,
      };
    case openBankingConstants.SET_PENDING_ACCOUNT_REFRESH:
      return {
        ...state,
        pendingAccountRefresh: {connectionId: action.connectionId, accountId: action.accountId},
      };
    case openBankingConstants.CLEAR_PENDING_ACCOUNT_REFRESH:
      return {
        ...state,
        pendingAccountRefresh: null,
      };
    case openBankingConstants.SET_ACCOUNT_ACCESS:
      return {
        ...state,
        accountAccessIds: data ?? [],
        hasAccountAccess: (data?.length || 0) > 0,
        accountAccessLoaded: true,
      };
    case openBankingConstants.ADD_ACCOUNT_ACCESS: {
      const accountAccessIds = [...new Set([...state.accountAccessIds, ...(data ?? [])])];
      return {
        ...state,
        accountAccessIds,
        hasAccountAccess: accountAccessIds.length > 0,
        accountAccessLoaded: true,
      };
    }
    case openBankingConstants.SET_OB_PAYMENT_METHODS:
      return {
        ...state,
        obPaymentMethodOptions: syncObOptionsWithAccounts(data ?? [], state.accounts),
      };
    case openBankingConstants.APPEND_OB_PAYMENT_METHODS: {
      const merged = [
        ...new Map([...state.obPaymentMethodOptions, ...(data ?? [])].map((o) => [o.value, o])).values(),
      ];
      return {
        ...state,
        // Sync against already-loaded accounts so options appended after the
        // accounts list adopt current account names, not their build-time snapshot.
        obPaymentMethodOptions: syncObOptionsWithAccounts(merged, state.accounts),
      };
    }
    case openBankingConstants.CLEAR_OPEN_BANKING_DATA:
      return openBankingState;
    default:
      return state;
  }
};

export default openBankingReducer;
